/**
 * A small flight recorder for the app's own slowness.
 *
 * "It froze last night on my box" is not reproducible, and the devices it
 * happens on are the ones nobody can attach a laptop to. So the page keeps a
 * rolling record of the things that precede a freeze — tasks that blocked the
 * main thread, how long each screen took to appear, and the heap — and the
 * remote debug server reads it back at /perf, after the fact.
 *
 * It must cost nothing: one PerformanceObserver, a bounded array, and a heap
 * sample every 20 seconds. Nothing is sent anywhere; it lives in memory until
 * the app closes, and it holds no titles, URLs or anything else about what is
 * being watched.
 */

export interface PerfEntry {
  /** ms since the page opened, rounded. */
  at: number;
  kind: "longtask" | "route" | "heap" | "frame";
  /** Duration for a task, a route or a frame, megabytes for a heap sample. */
  value: number;
  /**
   * The route's path, or for a frame the split that explains it; never a
   * stream URL or a title.
   */
  label?: string;
  /**
   * For a slow frame: the script that ran longest in it, as a CODE position —
   * function name, bundle file and character offset — which `dbg frames` maps
   * back to source. Never the invoker's text, which can carry an element id or
   * a URL.
   */
  source?: { fn: string; file: string; pos: number; type: string; ms: number };
}

/**
 * Frames are a second busy stream alongside the tasks, and the two evict each
 * other: the projector's last record already held 223 long tasks in 300 slots.
 * Six hundred small objects is still nothing, and it keeps both streams long
 * enough to cover the minutes a catalog ingest takes.
 */
const MAX_ENTRIES = 600;
const HEAP_EVERY_MS = 20_000;
/** Below this a "long" task is just ordinary work. */
const LONGTASK_MS = 50;
/** Below this a slow frame is not worth a line in the record. */
const LONGFRAME_MS = 100;

const entries: PerfEntry[] = [];
let started = false;
/** When the last long task and the last slow frame ENDED (performance.now()); -1 before any. */
let lastLongTaskEnd = -1;
let lastSlowFrameEnd = -1;

/**
 * How long the page has been quiet: ms since the last long task and the last
 * slow frame ended, or null if there has been none. What `dbg idle` waits on —
 * a measurement taken while the page is still busy measures the busyness.
 */
export function quietFor(): { longTaskMs: number | null; slowFrameMs: number | null } {
  const t = performance.now();
  return {
    longTaskMs: lastLongTaskEnd < 0 ? null : Math.round(t - lastLongTaskEnd),
    slowFrameMs: lastSlowFrameEnd < 0 ? null : Math.round(t - lastSlowFrameEnd),
  };
}

function push(entry: PerfEntry): void {
  entries.push(entry);
  if (entries.length > MAX_ENTRIES) entries.splice(0, entries.length - MAX_ENTRIES);
}

function now(): number {
  return Math.round(performance.now());
}

/** Record how long a screen took to become useful. Called by the router. */
export function recordRoute(path: string, ms: number): void {
  push({ at: now(), kind: "route", value: Math.round(ms), label: path });
}

interface MemoryInfo {
  usedJSHeapSize: number;
}

export function startPerfLog(): void {
  if (started || typeof window === "undefined") return;
  started = true;

  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        lastLongTaskEnd = Math.max(lastLongTaskEnd, entry.startTime + entry.duration);
        if (entry.duration < LONGTASK_MS) continue;
        push({ at: Math.round(entry.startTime), kind: "longtask", value: Math.round(entry.duration) });
      }
    });
    observer.observe({ entryTypes: ["longtask"] });
  } catch {
    // Browsers without the longtask entry type simply record less.
  }

  // A long task says the main thread was busy; it never says WITH WHAT, and a
  // task that is mostly style, layout or paint shows up as idle in a JS
  // profile — which is exactly where the last round of measuring ran out of
  // road (docs/archive/status-2026-09-20.md: ~6.1 s of startup tasks the profiler
  // reported as idle). `long-animation-frame` splits the frame into the parts
  // that caused it, so what is left after a fix can be named instead of
  // guessed at.
  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as LongAnimationFrame[]) {
        if (entry.duration < LONGFRAME_MS) continue;
        lastSlowFrameEnd = Math.max(lastSlowFrameEnd, entry.startTime + entry.duration);
        const source = frameSource(entry);
        push({
          at: Math.round(entry.startTime),
          kind: "frame",
          value: Math.round(entry.duration),
          label: frameSplit(entry),
          ...(source ? { source } : {}),
        });
      }
    });
    observer.observe({ type: "long-animation-frame", buffered: true });
  } catch {
    // Older WebViews have no long-animation-frame; the longtask lines remain.
  }

  const memory = (performance as Performance & { memory?: MemoryInfo }).memory;
  if (memory) {
    const sample = () => push({ at: now(), kind: "heap", value: Math.round(memory.usedJSHeapSize / 1048576) });
    sample();
    setInterval(sample, HEAP_EVERY_MS);
  }

  // What the debug server reads. Methods rather than the array itself, so the
  // shape can change without the laptop side guessing at it.
  (window as Window & { __perf?: PerfApi }).__perf = {
    read: () => ({ since: now(), entries: entries.slice() }),
    profile: takeProfile,
  };
}

interface PerfApi {
  read: () => { since: number; entries: PerfEntry[] };
  profile: (ms: number, sampleIntervalMs?: number) => Promise<unknown>;
}

/**
 * The shape of a long-animation-frame entry, as much of it as is used here.
 * Not in this TypeScript's DOM library yet.
 */
interface LongAnimationFrame extends PerformanceEntry {
  renderStart?: number;
  styleAndLayoutStart?: number;
  blockingDuration?: number;
  scripts?: Array<{
    duration?: number;
    invoker?: string;
    invokerType?: string;
    sourceURL?: string;
    sourceFunctionName?: string;
    sourceCharPosition?: number;
  }>;
}

/**
 * The longest script in a slow frame, as a code position (see PerfEntry.source).
 * Only our own bundle files are named — anything else (an iframe's script, an
 * extension) is reported as "other", never by URL.
 */
function frameSource(entry: LongAnimationFrame): PerfEntry["source"] | null {
  let top: NonNullable<LongAnimationFrame["scripts"]>[number] | null = null;
  for (const script of entry.scripts ?? []) {
    if (!top || (script.duration ?? 0) > (top.duration ?? 0)) top = script;
  }
  if (!top || !top.duration) return null;
  const file = /\/assets\/([^/?#]+\.js)/.exec(top.sourceURL ?? "")?.[1] ?? "other";
  return {
    fn: top.sourceFunctionName || "(anonymous)",
    file,
    pos: typeof top.sourceCharPosition === "number" ? top.sourceCharPosition : -1,
    type: top.invokerType ?? "unknown",
    ms: Math.round(top.duration),
  };
}

/**
 * Where a slow frame went, as a short label: how long scripts ran, how long
 * rendering took, and how much of it blocked. Durations only — an invoker or a
 * source URL could name what the viewer is watching, and nothing here is
 * allowed to.
 */
function frameSplit(entry: LongAnimationFrame): string {
  const start = entry.startTime;
  const end = start + entry.duration;
  const renderStart = entry.renderStart && entry.renderStart > 0 ? entry.renderStart : end;
  const styleStart = entry.styleAndLayoutStart && entry.styleAndLayoutStart > 0 ? entry.styleAndLayoutStart : end;
  const scripts = (entry.scripts ?? []).reduce((total, script) => total + Math.round(script.duration ?? 0), 0);
  const parts = [
    `script ${scripts}`,
    `pre-render ${Math.max(0, Math.round(renderStart - start))}`,
    `style+layout ${Math.max(0, Math.round(end - styleStart))}`,
  ];
  if (entry.blockingDuration) parts.push(`blocking ${Math.round(entry.blockingDuration)}`);
  return parts.join(", ");
}

interface ProfilerLike {
  stop: () => Promise<unknown>;
}

/**
 * A CPU profile of the page itself, using the browser's JS self-profiling API.
 *
 * The obvious route — the WebView's DevTools socket — is closed to us: it
 * accepts root and the adb shell only, so a TV box with no adb cannot be
 * profiled that way at all. This API is in the same WebView and needs only a
 * `Document-Policy: js-profiling` header, which MainActivity now adds to the
 * app's own document. The result is a sample trace; scripts/profile-report.mjs
 * maps its frames back through the bundle's source map.
 */
export async function takeProfile(ms: number, sampleIntervalMs = 10): Promise<unknown> {
  const Ctor = (window as Window & { Profiler?: new (options: { sampleInterval: number; maxBufferSize: number }) => ProfilerLike })
    .Profiler;
  if (!Ctor) throw new Error("this WebView has no JS self-profiling API");
  const samples = Math.ceil(ms / Math.max(1, sampleIntervalMs)) + 1000;
  const profiler = new Ctor({ sampleInterval: sampleIntervalMs, maxBufferSize: samples });
  await new Promise((resolve) => setTimeout(resolve, ms));
  return profiler.stop();
}
